import toast from 'react-hot-toast'
import { Chip } from '@/components/commons/chip'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ChevronDownIcon, XCloseIcon } from '@/components/ui/icons'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import { useRemoveRetouchingQaFlag } from '@/features/photoset/hooks/use-remove-retouching-qa-flag'
import { useRemoveImagesFlag } from '@/features/photoset/queries/use-remove-images-flag'
import {
	type Image,
	type Photoset,
	type PhotosetSpace,
	StepKey
} from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

export const GalleryRetouchingQaSidebar = ({
	currentImage,
	photosetId,
	photosetSpaceId
}: {
	photosetId: Photoset['id']
	currentImage: Omit<Image, 'space'>
	photosetSpaceId: PhotosetSpace['id']
}) => {
	const { t } = useTranslation()

	const { onRemoveFlagCb } = useRemoveRetouchingQaFlag({
		photosetId,
		photosetSpaceId
	})

	const removeImagesFlag = useRemoveImagesFlag({
		stepId: StepKey['retouching-qa'],
		photosetSpaceIds: [photosetSpaceId],
		options: {
			onError: () => toast.error(t('errors:default')),
			onSuccess: onRemoveFlagCb
		}
	})

	return (
		<div className='flex flex-col gap-4 p-4'>
			<FlagsPopover
				photosetSpaceIds={[photosetSpaceId]}
				selectedImagesIds={[currentImage.id]}
				stepId={StepKey['retouching-qa']}
				trigger={
					<ButtonLegacy
						className='w-full cursor-pointer justify-between font-normal text-neutral-500 shadow-none data-[state=open]:shadow-[0_0_0_4px_var(--color-primary-200)] [&_svg]:transition-transform [&_svg]:duration-200 [&_svg]:ease-in-out data-[state=open]:[&_svg]:rotate-180'
						type='button'
						variant='secondary'
					>
						<span>
							{t('photoset:retouching_qa.add_flag_trigger')}
						</span>
						<ChevronDownIcon />
					</ButtonLegacy>
				}
				triggerWidth={'100%'}
				types={['visual_retouching']}
				width={'308px'}
			/>

			<div className='flex flex-wrap gap-1.5'>
				{currentImage.flags
					.filter((flag) => flag.type === 'visual_retouching')
					.map((flag) => (
						<Chip
							endSlot={
								<button
									onClick={() => {
										removeImagesFlag.mutate({
											flag,
											imageIds: [currentImage.id]
										})
									}}
									tabIndex={-1}
									type='button'
								>
									<XCloseIcon />
								</button>
							}
							isActive
							key={flag.id}
							label={t(`photoset:flags.${flag.key_name}.label`)}
							variant='info'
						/>
					))}
			</div>
		</div>
	)
}
